import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import {
  FiCheckCircle, FiEdit2, FiUpload, FiType, FiX, FiRefreshCw,
  FiAlertCircle, FiDownload, FiChevronLeft, FiChevronRight,
  FiCalendar, FiUser, FiArrowRight, FiMaximize2,
} from 'react-icons/fi';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { signingAPI } from '../../services/api';
import { formatDate } from '../../utils/dateFormat';
import './SigningPage.scss';

const PDF_WORKER_URL = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;
// Default: point pdf.js at the static worker file. This only works if the server
// labels .mjs with a JS MIME type; the effect below replaces it with a blob URL
// that always carries text/javascript, so the worker loads regardless.
pdfjs.GlobalWorkerOptions.workerSrc = PDF_WORKER_URL;

const DEFAULTS = { signature: { w: 160, h: 55 }, name: { w: 130, h: 22 }, date: { w: 110, h: 22 } };
const MIN_SIZE = 20;

function SigningPage() {
  const { accessToken } = useParams();
  const { t } = useTranslation();
  const [stage, setStage] = useState('loading');
  const [accessInfo, setAccessInfo] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [resending, setResending] = useState(false);

  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfError, setPdfError] = useState(null);
  const [workerReady, setWorkerReady] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfPageWidth, setPdfPageWidth] = useState(700);
  const [pdfDims, setPdfDims] = useState(null);
  const pdfAreaRef = useRef(null);

  const [sigMode, setSigMode] = useState(null);
  const [sigData, setSigData] = useState(null);
  const [typedName, setTypedName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [allSigned, setAllSigned] = useState(false);

  const [sigPlacement, setSigPlacement] = useState(null);
  const [namePlacement, setNamePlacement] = useState(null);
  const [datePlacement, setDatePlacement] = useState(null);

  // drag: from panel or repositioning
  const [dragType, setDragType] = useState(null);
  const [dragPos, setDragPos] = useState(null);
  const [dragTarget, setDragTarget] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  // resize
  const [resizeTarget, setResizeTarget] = useState(null);
  const [resizeStart, setResizeStart] = useState(null);

  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const lastPt = useRef(null);

  const todayStr = formatDate(new Date());
  const signerName = accessInfo?.signerName || '';
  const accentColor = accessInfo?.accentColor || '#6366f1';

  const getPlacement = (t) => t === 'signature' ? sigPlacement : t === 'name' ? namePlacement : datePlacement;
  const getSetter = (t) => t === 'signature' ? setSigPlacement : t === 'name' ? setNamePlacement : setDatePlacement;

  // ── PDF.js worker — load it as a blob with an explicit JS MIME type so it
  // works regardless of how the server labels .mjs files (nginx may serve them
  // as application/octet-stream, which browsers refuse to run as a worker). ──
  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    fetch(PDF_WORKER_URL, { cache: 'reload' })
      .then((r) => { if (!r.ok) throw new Error(`worker ${r.status}`); return r.text(); })
      .then((src) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(new Blob([src], { type: 'text/javascript' }));
        pdfjs.GlobalWorkerOptions.workerSrc = objectUrl;
        setWorkerReady(true);
      })
      .catch(() => {
        // Fall back to the direct URL (works when the server labels .mjs right).
        if (cancelled) return;
        pdfjs.GlobalWorkerOptions.workerSrc = PDF_WORKER_URL;
        setWorkerReady(true);
      });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, []);

  // ── Load ──
  // Read a possible JSON `{ error }` body out of a Blob response so the real
  // backend reason is shown instead of a generic "failed to load".
  const errorFromBlob = async (blob, fallback) => {
    try { const j = JSON.parse(await blob.text()); if (j?.error) return j.error; } catch { /* not JSON */ }
    return fallback;
  };

  const loadPdf = useCallback(() => {
    setPdfError(null);
    setPdfBlobUrl(null);
    const fallback = t('signing.failedToLoadPdf', { defaultValue: 'Failed to load the document. Please try again.' });
    signingAPI.getPdf(accessToken)
      .then(async ({ data }) => {
        // Verify the payload is actually a PDF. A JSON/HTML body served with a
        // 2xx status (e.g. a proxy fallback) is not a valid PDF and would make
        // react-pdf fail opaquely — surface the real reason instead.
        const head = await data.slice(0, 5).text().catch(() => '');
        if (!head.startsWith('%PDF')) {
          setPdfError(await errorFromBlob(data, fallback));
          return;
        }
        setPdfBlobUrl(URL.createObjectURL(data));
      })
      .catch(async (err) => {
        const data = err.response?.data;
        if (data instanceof Blob) setPdfError(await errorFromBlob(data, fallback));
        else setPdfError(data?.error || fallback);
      });
  }, [accessToken, t]);

  useEffect(() => {
    signingAPI.verifyAccess(accessToken).then(({ data }) => {
      setAccessInfo(data);
      if (data.status === 'signed') setStage('done');
      else if (data.requiresOtp) setStage('otp');
      else { setStage('signing'); loadPdf(); }
    }).catch((err) => {
      setAccessInfo({ error: err.response?.data?.error || 'Invalid or expired signing link.' });
      setStage('error');
    });
  }, [accessToken, loadPdf]);

  useEffect(() => {
    const update = () => {
      if (pdfAreaRef.current) setPdfPageWidth(Math.min(Math.max(pdfAreaRef.current.clientWidth - 60, 450), 950));
    };
    update(); window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [stage]);

  // ── OTP ──
  const handleVerifyOtp = async () => {
    if (!otpCode.trim()) return; setOtpSending(true); setOtpError('');
    try { await signingAPI.verifyOtp(accessToken, otpCode.trim()); setStage('signing'); loadPdf(); }
    catch (err) { setOtpError(err.response?.data?.error || 'Verification failed.'); }
    finally { setOtpSending(false); }
  };
  const handleResendOtp = async () => {
    setResending(true);
    try { await signingAPI.resendOtp(accessToken); setOtpError(''); setOtpCode(''); }
    catch (err) { setOtpError(err.response?.data?.error || 'Failed to resend.'); }
    finally { setResending(false); }
  };

  // ── Canvas ──
  const initCanvas = useCallback(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.strokeStyle = '#1e293b'; ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  }, []);
  useEffect(() => { if (sigMode === 'draw') initCanvas(); }, [sigMode, initCanvas]);

  const canvasPt = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - r.left) * (c.width / r.width), y: (clientY - r.top) * (c.height / r.height) };
  };
  const startDraw = (e) => {
    isDrawing.current = true;
    lastPt.current = canvasPt(e);
  };
  const draw = (e) => {
    if (!isDrawing.current) return; e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pt = canvasPt(e);
    ctx.beginPath(); ctx.moveTo(lastPt.current.x, lastPt.current.y); ctx.lineTo(pt.x, pt.y); ctx.stroke();
    lastPt.current = pt;
  };
  const endDraw = () => { isDrawing.current = false; if (canvasRef.current) setSigData(canvasRef.current.toDataURL('image/png')); };

  const genTyped = useCallback(() => {
    if (!typedName.trim()) { setSigData(null); return; }
    const c = document.createElement('canvas'); c.width = 420; c.height = 100;
    const ctx = c.getContext('2d'); ctx.clearRect(0, 0, 420, 100);
    ctx.font = 'italic 38px "Dancing Script", "Brush Script MT", cursive, serif';
    ctx.fillStyle = '#1e293b'; ctx.textBaseline = 'middle'; ctx.fillText(typedName, 16, 50);
    setSigData(c.toDataURL('image/png'));
  }, [typedName]);
  useEffect(() => { if (sigMode === 'type') genTyped(); }, [typedName, sigMode, genTyped]);

  const handleUpload = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader(); r.onload = (ev) => setSigData(ev.target.result); r.readAsDataURL(f);
  };

  // ── Coords ──
  const sc = useCallback((rect) => pdfDims ? { sx: pdfDims.width / rect.width, sy: pdfDims.height / rect.height } : { sx: 1, sy: 1 }, [pdfDims]);
  const toPdfY = useCallback((ry, h, rect) => { const { sy } = sc(rect); return (pdfDims ? pdfDims.height : rect.height) - ry * sy - h; }, [sc, pdfDims]);

  // ── Drag from panel ──
  const handlePanelDragStart = (type) => (e) => {
    if (type === 'signature' && !sigData) return;
    e.preventDefault();
    setDragType(type);
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    setDragPos({ x: cx, y: cy });
  };

  // ── Drag placed element ──
  const handleElDragStart = (target) => (e) => {
    e.stopPropagation();
    const p = getPlacement(target); if (!p) return;
    setDragTarget(target);
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    setDragOffset({ x: cx - p._rx, y: cy - p._ry });
  };

  // ── Resize handle ──
  const handleResizeStart = (target) => (e) => {
    e.stopPropagation(); e.preventDefault();
    const p = getPlacement(target); if (!p) return;
    setResizeTarget(target);
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    setResizeStart({ cx, cy, w: p._rw, h: p._rh, pdfW: p.width, pdfH: p.height });
  };

  // ── Global mouse/touch ──
  const handleGlobalMove = useCallback((e) => {
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;

    if (dragType) { e.preventDefault(); setDragPos({ x: cx, y: cy }); return; }

    if (resizeTarget && resizeStart) {
      e.preventDefault();
      const p = getPlacement(resizeTarget); if (!p) return;
      const pageEl = document.querySelector('.sp2__page-wrap'); if (!pageEl) return;
      const rect = pageEl.getBoundingClientRect();
      const { sx, sy } = sc(rect);
      const dx = cx - resizeStart.cx;
      const dy = cy - resizeStart.cy;
      const newRW = Math.max(MIN_SIZE, resizeStart.w + dx);
      const newRH = Math.max(MIN_SIZE, resizeStart.h + dy);
      const newPdfW = newRW * sx;
      const newPdfH = newRH * sy;
      const setter = getSetter(resizeTarget);
      const newPdfY = toPdfY(p._ry, newPdfH, rect);
      setter(prev => ({ ...prev, _rw: newRW, _rh: newRH, width: newPdfW, height: newPdfH, y: newPdfY }));
      return;
    }

    if (dragTarget) {
      e.preventDefault();
      const p = getPlacement(dragTarget); if (!p) return;
      const pageEl = document.querySelector('.sp2__page-wrap'); if (!pageEl) return;
      const rect = pageEl.getBoundingClientRect();
      const { sx } = sc(rect);
      let rx = Math.max(0, Math.min(cx - dragOffset.x, rect.width - p._rw));
      let ry = Math.max(0, Math.min(cy - dragOffset.y, rect.height - p._rh));
      const pdfX = rx * sx;
      const pdfY = toPdfY(ry, p.height, rect);
      const setter = getSetter(dragTarget);
      setter(prev => ({ ...prev, x: pdfX, y: pdfY, _rx: rx, _ry: ry }));
    }
    // eslint-disable-next-line
  }, [dragType, dragTarget, resizeTarget, resizeStart, sigPlacement, namePlacement, datePlacement, dragOffset, sc, toPdfY]);

  const handleGlobalEnd = useCallback((e) => {
    if (dragType) {
      const pageEl = document.querySelector('.sp2__page-wrap');
      if (pageEl) {
        const rect = pageEl.getBoundingClientRect();
        const cx = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
        const cy = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;
        if (cx >= rect.left && cx <= rect.right && cy >= rect.top && cy <= rect.bottom) {
          const { sx, sy } = sc(rect);
          const dims = DEFAULTS[dragType];
          const rw = dims.w / sx, rh = dims.h / sy;
          const rx = Math.max(0, Math.min(cx - rect.left - rw / 2, rect.width - rw));
          const ry = Math.max(0, Math.min(cy - rect.top - rh / 2, rect.height - rh));
          const pdfX = rx * sx;
          const pdfY = toPdfY(ry, dims.h, rect);
          const pl = { page: currentPage, x: pdfX, y: pdfY, width: dims.w, height: dims.h, _rx: rx, _ry: ry, _rw: rw, _rh: rh };
          getSetter(dragType)(pl);
        }
      }
      setDragType(null); setDragPos(null);
    }
    if (dragTarget) setDragTarget(null);
    if (resizeTarget) { setResizeTarget(null); setResizeStart(null); }
    // eslint-disable-next-line
  }, [dragType, dragTarget, resizeTarget, currentPage, sc, toPdfY]);

  useEffect(() => {
    if (dragType || dragTarget || resizeTarget) {
      const opts = { passive: false };
      window.addEventListener('mousemove', handleGlobalMove);
      window.addEventListener('mouseup', handleGlobalEnd);
      window.addEventListener('touchmove', handleGlobalMove, opts);
      window.addEventListener('touchend', handleGlobalEnd);
      return () => {
        window.removeEventListener('mousemove', handleGlobalMove);
        window.removeEventListener('mouseup', handleGlobalEnd);
        window.removeEventListener('touchmove', handleGlobalMove);
        window.removeEventListener('touchend', handleGlobalEnd);
      };
    }
  }, [dragType, dragTarget, resizeTarget, handleGlobalMove, handleGlobalEnd]);

  // ── Submit ──
  const handleSubmit = async () => {
    if (!sigData || !sigPlacement) return;
    setSubmitting(true);
    try {
      const payload = {
        signatureData: sigData, signatureType: sigMode,
        placement: { page: sigPlacement.page, x: sigPlacement.x, y: sigPlacement.y, width: sigPlacement.width, height: sigPlacement.height },
      };
      if (namePlacement) payload.namePlacement = { page: namePlacement.page, x: namePlacement.x, y: namePlacement.y, width: namePlacement.width, height: namePlacement.height };
      if (datePlacement) payload.datePlacement = { page: datePlacement.page, x: datePlacement.x, y: datePlacement.y, width: datePlacement.width, height: datePlacement.height };
      const res = await signingAPI.submitSignature(accessToken, payload);
      setAllSigned(res.data.allSigned); setStage('done');
    } catch (err) { alert(err.response?.data?.error || t('signing.failedToSubmit')); }
    finally { setSubmitting(false); }
  };

  const handleDownloadSigned = async () => {
    try {
      const { data } = await signingAPI.getSignedPdf(accessToken);
      const url = URL.createObjectURL(data);
      const a = document.createElement('a'); a.href = url;
      a.download = `${accessInfo?.documentTitle || 'signed'}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { alert(t('signing.signedDocNotAvailable')); }
  };

  // ── Contextual hints (action-based only) ──
  const getHint = () => {
    if (!sigMode) return { title: t('signing.getStarted'), text: t('signing.getStartedText') };
    if (sigData && !sigPlacement) return { title: t('signing.signatureReady'), text: t('signing.signatureReadyText') };
    if (sigPlacement && !namePlacement && !datePlacement) return { title: t('signing.signaturePlaced'), text: t('signing.signaturePlacedText') };
    if (sigPlacement) return { title: t('signing.almostDone'), text: t('signing.almostDoneText') };
    return null;
  };
  const hint = getHint();

  const canSubmit = sigData && sigPlacement;
  const renderLogo = () => (
    <div className="sp2__brand">
      {accessInfo?.tenantLogoUrl
        ? <img src={accessInfo.tenantLogoUrl} alt="" className="sp2__brand-logo" />
        : <div className="sp2__brand-icon" style={{ background: accentColor }}>{(accessInfo?.tenantName || 'C')[0]}</div>}
      <span className="sp2__brand-name">{accessInfo?.tenantName || t('signing.contractSigning')}</span>
    </div>
  );

  // ── Non-signing stages ──
  if (stage === 'loading') return (
    <div className="sp2 sp2--center"><div className="sp2__loader"><div className="sp2__loader-ring" style={{ borderTopColor: accentColor }} /></div><p className="sp2__loader-text">{t('signing.preparingDocument')}</p></div>
  );
  if (stage === 'error') return (
    <div className="sp2 sp2--center"><div className="sp2__status-card sp2__status-card--error"><div className="sp2__status-icon sp2__status-icon--error"><FiAlertCircle size={32} /></div><h2>{t('signing.unableToAccess')}</h2><p>{accessInfo?.error || t('signing.invalidOrExpired')}</p></div></div>
  );
  if (stage === 'otp') return (
    <div className="sp2 sp2--center">
      <div className="sp2__otp-card">
        {renderLogo()}
        <div className="sp2__otp-shield"><FiEdit2 size={24} /></div>
        <h2>{t('signing.verifyIdentity')}</h2>
        {/* SEC-10: signer name/email are server data and i18n has escapeValue:false —
            <Trans> renders the <strong> tags from the string as components and
            the values as text, instead of injecting them as HTML. */}
        <p>
          <Trans
            i18nKey="signing.otpPrompt"
            values={{ name: accessInfo?.signerName, email: accessInfo?.signerEmail }}
            components={{ strong: <strong /> }}
          />
        </p>
        <input type="text" className="sp2__otp-input" maxLength={6} value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))} onKeyDown={(e) => e.key === 'Enter' && handleVerifyOtp()} placeholder="------" autoFocus />
        {otpError && <p className="sp2__otp-error"><FiAlertCircle size={14} /> {otpError}</p>}
        <button className="sp2__btn sp2__btn--primary sp2__btn--full" onClick={handleVerifyOtp} disabled={otpSending || otpCode.length < 6} style={{ '--accent': accentColor }}>{otpSending ? t('signing.verifying') : t('signing.verifyAndContinue')}</button>
        <button className="sp2__btn sp2__btn--ghost" onClick={handleResendOtp} disabled={resending}><FiRefreshCw size={14} /> {resending ? t('signing.sending') : t('signing.resendCode')}</button>
      </div>
    </div>
  );
  if (stage === 'done') return (
    <div className="sp2 sp2--center">
      <div className="sp2__status-card sp2__status-card--success">
        {renderLogo()}
        <div className="sp2__status-icon sp2__status-icon--success"><FiCheckCircle size={40} /></div>
        <h2>{allSigned ? t('signing.allSignaturesComplete') : t('signing.signatureSubmitted')}</h2>
        {accessInfo?.documentTitle && <p className="sp2__status-doc">{accessInfo.documentTitle}</p>}
        <p>{allSigned ? t('signing.documentFullyExecuted') : t('signing.waitingForOtherSigners')}</p>
        {allSigned && <button className="sp2__btn sp2__btn--primary" onClick={handleDownloadSigned} style={{ '--accent': accentColor }}><FiDownload size={16} /> {t('signing.downloadSignedDocument')}</button>}
      </div>
    </div>
  );

  // ── Signing ──
  return (
    <div className="sp2 sp2--signing" style={{ '--accent': accentColor }}>
      {/* Header */}
      <header className="sp2__header">
        <div className="sp2__header-left">{renderLogo()}</div>
        <div className="sp2__header-center">
          <span className="sp2__header-doc">{accessInfo?.documentTitle || accessInfo?.contractName}</span>
        </div>
        <div className="sp2__header-right">
          <div className="sp2__signer-badge"><FiUser size={14} /><span>{signerName}</span></div>
        </div>
      </header>

      <div className="sp2__body">
        {/* ── Left Panel ── */}
        <aside className="sp2__panel">
          {/* Action-based hint */}
          {hint && (
            <div className="sp2__hint-card" key={hint.title}>
              <div className="sp2__hint-content">
                <strong>{hint.title}</strong>
                <p>{hint.text}</p>
              </div>
            </div>
          )}

          {/* Signature creation */}
          <div className="sp2__section">
            <h4 className="sp2__section-label">{t('signing.createSignature')}</h4>
            <div className="sp2__sig-tabs">
              <button className={`sp2__sig-tab ${sigMode === 'draw' ? 'sp2__sig-tab--active' : ''}`}
                onClick={() => { setSigMode('draw'); setSigData(null); }}>
                <FiEdit2 size={14} /><span>{t('signing.draw')}</span>
              </button>
              <button className={`sp2__sig-tab ${sigMode === 'type' ? 'sp2__sig-tab--active' : ''}`}
                onClick={() => { setSigMode('type'); setSigData(null); setTypedName(''); }}>
                <FiType size={14} /><span>{t('signing.type')}</span>
              </button>
              <button className={`sp2__sig-tab ${sigMode === 'upload' ? 'sp2__sig-tab--active' : ''}`}
                onClick={() => { setSigMode('upload'); setSigData(null); }}>
                <FiUpload size={14} /><span>{t('signing.upload')}</span>
              </button>
            </div>

            {sigMode === 'draw' && (
              <div className="sp2__sig-area">
                <canvas ref={canvasRef} width={380} height={120} className="sp2__canvas"
                  onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
                  onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw} />
                <button className="sp2__clear-btn" onClick={() => { initCanvas(); setSigData(null); }}><FiX size={12} /> {t('signing.clear')}</button>
              </div>
            )}
            {sigMode === 'type' && (
              <div className="sp2__sig-area">
                <input type="text" className="sp2__type-input" placeholder={t('signing.typeFullName')} value={typedName} onChange={(e) => setTypedName(e.target.value)} autoFocus />
                {sigData && <div className="sp2__sig-preview"><img src={sigData} alt="" /></div>}
              </div>
            )}
            {sigMode === 'upload' && (
              <div className="sp2__sig-area">
                <label className="sp2__upload-zone">
                  <FiUpload size={20} /><span>{t('signing.clickToUpload')}</span><span className="sp2__upload-sub">{t('signing.uploadFormats')}</span>
                  <input type="file" accept="image/*" onChange={handleUpload} hidden />
                </label>
                {sigData && <div className="sp2__sig-preview"><img src={sigData} alt="" /></div>}
              </div>
            )}
          </div>

          {/* Draggable fields */}
          <div className="sp2__section">
            <h4 className="sp2__section-label">{t('signing.dragOntoDocument')}</h4>
            <div className="sp2__fields">
              <div className={`sp2__field sp2__field--sig ${!sigData ? 'sp2__field--disabled' : ''} ${sigPlacement ? 'sp2__field--placed' : ''}`}
                onMouseDown={handlePanelDragStart('signature')} onTouchStart={handlePanelDragStart('signature')}>
                <div className="sp2__field-icon sp2__field-icon--sig"><FiEdit2 size={14} /></div>
                <div className="sp2__field-info">
                  <span>{t('signing.signature')}</span>
                  <small>{sigPlacement ? t('signing.placed') : sigData ? t('signing.readyToDrag') : t('signing.createFirst')}</small>
                </div>
                {sigPlacement ? <FiCheckCircle size={16} className="sp2__field-check" /> : <FiMaximize2 size={12} className="sp2__field-drag-icon" />}
              </div>
              <div className={`sp2__field sp2__field--name ${namePlacement ? 'sp2__field--placed' : ''}`}
                onMouseDown={handlePanelDragStart('name')} onTouchStart={handlePanelDragStart('name')}>
                <div className="sp2__field-icon sp2__field-icon--name"><FiUser size={14} /></div>
                <div className="sp2__field-info">
                  <span>{t('signing.fullName')}</span>
                  <small>{namePlacement ? t('signing.placed') : t('signing.optional')}</small>
                </div>
                {namePlacement ? <FiCheckCircle size={16} className="sp2__field-check" /> : <FiMaximize2 size={12} className="sp2__field-drag-icon" />}
              </div>
              <div className={`sp2__field sp2__field--date ${datePlacement ? 'sp2__field--placed' : ''}`}
                onMouseDown={handlePanelDragStart('date')} onTouchStart={handlePanelDragStart('date')}>
                <div className="sp2__field-icon sp2__field-icon--date"><FiCalendar size={14} /></div>
                <div className="sp2__field-info">
                  <span>{t('signing.dateSigned')}</span>
                  <small>{datePlacement ? t('signing.placed') : t('signing.optional')}</small>
                </div>
                {datePlacement ? <FiCheckCircle size={16} className="sp2__field-check" /> : <FiMaximize2 size={12} className="sp2__field-drag-icon" />}
              </div>
            </div>
          </div>

          {/* Submit */}
          <button className="sp2__btn sp2__btn--submit sp2__btn--full" onClick={handleSubmit} disabled={!canSubmit || submitting}>
            {submitting ? t('signing.submitting') : <>{t('signing.finishSigning')} <FiArrowRight size={16} /></>}
          </button>
        </aside>

        {/* ── PDF Area ── */}
        <main className="sp2__main" ref={pdfAreaRef}>
          {pdfBlobUrl && workerReady ? (
            <div className="sp2__pdf-scroll">
              {numPages > 1 && (
                <div className="sp2__page-nav">
                  <button disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}><FiChevronLeft size={18} /></button>
                  <span>{currentPage} / {numPages}</span>
                  <button disabled={currentPage >= numPages} onClick={() => setCurrentPage(p => p + 1)}><FiChevronRight size={18} /></button>
                </div>
              )}
              <Document file={pdfBlobUrl} onLoadSuccess={({ numPages: n }) => { setNumPages(n); setCurrentPage(1); }}
                onLoadError={(err) => { setPdfBlobUrl(null); setPdfError(err?.message || t('signing.failedToLoadPdf', { defaultValue: 'Failed to load the document. Please try again.' })); }}
                loading={<div className="sp2__pdf-loading"><div className="sp2__loader-ring" /><p>{t('signing.loading')}</p></div>}>
                <div className="sp2__page-wrap" style={{ position: 'relative', display: 'inline-block' }}>
                  <Page pageNumber={currentPage} width={pdfPageWidth}
                    onLoadSuccess={(p) => setPdfDims({ width: p.originalWidth, height: p.originalHeight })}
                    renderAnnotationLayer={true} renderTextLayer={true} />

                  {/* Overlays with resize handles */}
                  {sigPlacement?.page === currentPage && sigData && (
                    <div className="sp2__overlay sp2__overlay--sig" style={{ left: sigPlacement._rx, top: sigPlacement._ry, width: sigPlacement._rw, height: sigPlacement._rh }}
                      onMouseDown={handleElDragStart('signature')} onTouchStart={handleElDragStart('signature')}>
                      <img src={sigData} alt="" draggable={false} />
                      <button className="sp2__overlay-x" onClick={(e) => { e.stopPropagation(); setSigPlacement(null); }}><FiX size={10} /></button>
                      <div className="sp2__overlay-resize" onMouseDown={handleResizeStart('signature')} onTouchStart={handleResizeStart('signature')} />
                    </div>
                  )}
                  {namePlacement?.page === currentPage && (
                    <div className="sp2__overlay sp2__overlay--name" style={{ left: namePlacement._rx, top: namePlacement._ry, width: namePlacement._rw, height: namePlacement._rh }}
                      onMouseDown={handleElDragStart('name')} onTouchStart={handleElDragStart('name')}>
                      <span>{signerName}</span>
                      <button className="sp2__overlay-x" onClick={(e) => { e.stopPropagation(); setNamePlacement(null); }}><FiX size={10} /></button>
                      <div className="sp2__overlay-resize" onMouseDown={handleResizeStart('name')} onTouchStart={handleResizeStart('name')} />
                    </div>
                  )}
                  {datePlacement?.page === currentPage && (
                    <div className="sp2__overlay sp2__overlay--date" style={{ left: datePlacement._rx, top: datePlacement._ry, width: datePlacement._rw, height: datePlacement._rh }}
                      onMouseDown={handleElDragStart('date')} onTouchStart={handleElDragStart('date')}>
                      <span>{todayStr}</span>
                      <button className="sp2__overlay-x" onClick={(e) => { e.stopPropagation(); setDatePlacement(null); }}><FiX size={10} /></button>
                      <div className="sp2__overlay-resize" onMouseDown={handleResizeStart('date')} onTouchStart={handleResizeStart('date')} />
                    </div>
                  )}
                </div>
              </Document>
              {dragType && <div className="sp2__drop-hint">{t('signing.dropHere')}</div>}
            </div>
          ) : pdfError ? (
            <div className="sp2__pdf-loading">
              <FiAlertCircle size={30} style={{ color: '#ef4444' }} />
              <p>{pdfError}</p>
              <button className="sp2__btn sp2__btn--primary" onClick={loadPdf}>
                <FiRefreshCw size={15} /> {t('signing.retry', { defaultValue: 'Try again' })}
              </button>
            </div>
          ) : (
            <div className="sp2__pdf-loading"><div className="sp2__loader-ring" /><p>{t('signing.loadingDocument')}</p></div>
          )}
        </main>
      </div>

      {/* Drag ghost */}
      {dragType && dragPos && (
        <div className="sp2__drag-ghost" style={{ left: dragPos.x, top: dragPos.y }}>
          {dragType === 'signature' && sigData && <img src={sigData} alt="" />}
          {dragType === 'name' && <span className="sp2__ghost-text"><FiUser size={11} /> {signerName}</span>}
          {dragType === 'date' && <span className="sp2__ghost-text"><FiCalendar size={11} /> {todayStr}</span>}
        </div>
      )}
    </div>
  );
}

export default SigningPage;
